import React, { useState, useMemo } from 'react';
import api from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DOCUMENT_TYPE_LABELS = {
  prescription: 'Prescription',
  lab_report: 'Lab Report',
  discharge_summary: 'Discharge Summary',
  xray_scan: 'X-Ray/Scan',
  insurance: 'Insurance',
  vaccination: 'Vaccination',
  other: 'Other'
};

const DOCUMENT_TYPE_COLORS = {
  prescription: '#ed8936',
  lab_report: '#4299e1',
  discharge_summary: '#9f7aea',
  xray_scan: '#38b2ac',
  insurance: '#48bb78',
  vaccination: '#ecc94b',
  other: '#a0aec0'
};

const FILE_ICONS = {
  pdf: '📄',
  image: '🖼️',
  word: '📝',
  default: '📋'
};

const DOCUMENT_TYPE_ICONS = {
  prescription: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v8M8 6h8"/><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M9 14h6M9 18h4"/>
    </svg>
  ),
  lab_report: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2v6L4 18a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-10V2"/><path d="M7 2h10"/><path d="M7 16h10"/>
    </svg>
  ),
  discharge_summary: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 15h6M9 11h2"/>
    </svg>
  ),
  xray_scan: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 7v10M8 9v6M16 9v6"/>
    </svg>
  ),
  insurance: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  vaccination: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m18 2 4 4M17 7l3-3M19 9 9 19l-4 1 1-4L16 6l3 3z"/>
    </svg>
  ),
  other: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M13 2v7h7"/>
    </svg>
  )
};

// Small icon button with hover state for table row actions
const IconButton = ({ onClick, title, disabled, hoverBg, hoverColor, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      color: disabled ? '#cbd5e0' : '#718096',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s'
    }}
    onMouseEnter={(e) => {
      if (disabled) return;
      e.currentTarget.style.background = hoverBg;
      e.currentTarget.style.color = hoverColor;
      e.currentTarget.style.borderColor = hoverColor + '40';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = disabled ? '#cbd5e0' : '#718096';
      e.currentTarget.style.borderColor = '#e2e8f0';
    }}
  >
    {children}
  </button>
);

function DocumentList({ patientId, documents, onDelete, onRefresh }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [expandedSections, setExpandedSections] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, json: '' });

  // Group documents by type
  const documentsByType = useMemo(() => {
    const groups = {};
    documents.forEach(doc => {
      if (!groups[doc.document_type]) {
        groups[doc.document_type] = [];
      }
      groups[doc.document_type].push(doc);
    });
    return groups;
  }, [documents]);

  const toggleSection = (type) => {
    setExpandedSections(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const getNoteSummary = (notes) => {
    if (!notes) return '-';
    try {
	      const parsed = JSON.parse(notes);
	      if (typeof parsed === 'object') {
		      const parts = [];
		      const labCount = (parsed.lab_results || []).length;
		      const vitalCount = (parsed.vitals || []).length;
		      const radCount = (parsed.radiology || []).length;
		      const rxCount = (parsed.prescriptions || []).length;
		      if (labCount > 0) parts.push(`${labCount} lab results`);
		      if (vitalCount > 0) parts.push(`${vitalCount} vitals`);
		      if (radCount > 0) parts.push(`${radCount} radiology`);
		      if (rxCount > 0) parts.push(`${rxCount} prescriptions`);
		      if (parsed.report_date) parts.push(parsed.report_date);
		      if (parsed.doctor_name) parts.push(parsed.doctor_name);
		      return parts.length > 0 ? parts.join(' • ') : 'Parsed report';
	      }
	    } catch {
	      return notes.length > 80 ? notes.substring(0, 80) + '...' : notes;
	    }
	    return '-';
	  };

	  const getExtractedMedicalData = (notes) => {
	    if (!notes) return '-';
	    // Split by CLINICAL_NOTES: separator to extract just the JSON part
	    const parts = notes.split('\n\nCLINICAL_NOTES:');
	    try {
	      const parsed = JSON.parse(parts[0].trim());
	      return JSON.stringify(parsed, null, 2);
	    } catch {
	      // If it's not valid JSON, return the raw notes (might be plain text)
	      return parts[0].length > 100 ? parts[0].substring(0, 100) + '...' : parts[0];
	    }
	  };

	  const getClinicalNotes = (notes) => {
	    if (!notes) return '-';
	    // Extract clinical notes part after CLINICAL_NOTES: separator
	    const parts = notes.split('\n\nCLINICAL_NOTES:');
	    if (parts.length > 1) {
	      const clinical = parts[1].trim();
	      return clinical.length > 80 ? clinical.substring(0, 80) + '...' : clinical;
	    }
	    return '-';
	  };

  const getExtractedDataSummary = (notes) => {
    if (!notes) return '-';
    const parts = notes.split('\n\nCLINICAL_NOTES:');
    try {
      const parsed = JSON.parse(parts[0].trim());
      if (typeof parsed === 'object') {
        const counts = [];
        const labCount = (parsed.lab_results || []).length;
        const vitalCount = (parsed.vitals || []).length;
        const radCount = (parsed.radiology || []).length;
        const rxCount = (parsed.prescriptions || []).length;
        if (labCount > 0) counts.push(`${labCount} lab result${labCount !== 1 ? 's' : ''}`);
        if (vitalCount > 0) counts.push(`${vitalCount} vital${vitalCount !== 1 ? 's' : ''}`);
        if (radCount > 0) counts.push(`${radCount} radiology report${radCount !== 1 ? 's' : ''}`);
        if (rxCount > 0) counts.push(`${rxCount} prescription${rxCount !== 1 ? 's' : ''}`);
        return counts.length > 0 ? counts.join(', ') : 'No medical data extracted';
      }
    } catch {
      return notes.length > 50 ? notes.substring(0, 50) + '...' : notes;
    }
    return '-';
  };

  const handleSummaryMouseEnter = (e, json) => {
    if (!json || json === '-') return;
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      show: true,
      x: rect.left + rect.width / 2,
      y: rect.top,
      json
    });
  };

  const handleSummaryMouseLeave = () => {
    setTooltip(prev => ({ ...prev, show: false }));
  };

  const handleTooltipMouseEnter = () => {
    setTooltip(prev => ({ ...prev, show: true }));
  };

  const handleTooltipMouseLeave = () => {
    setTooltip(prev => ({ ...prev, show: false }));
  };

  const downloadJson = (jsonStr, originalFilename) => {
    try {
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = originalFilename.includes('.') ? originalFilename.substring(0, originalFilename.lastIndexOf('.')) : originalFilename;
      a.download = `${baseName}_extracted.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download JSON:', err);
      alert('Failed to download file');
    }
  };

  const getFileIcon = (mimeType, filename) => {
    if (mimeType.includes('pdf')) return FILE_ICONS.pdf;
    if (mimeType.includes('image')) return FILE_ICONS.image;
    if (mimeType.includes('word') || filename.endsWith('.doc') || filename.endsWith('.docx')) {
      return FILE_ICONS.word;
    }
    return FILE_ICONS.default;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 bytes';
    const k = 1024;
    const sizes = ['bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  const handleView = (documentId) => {
    window.open(`${API_URL}/documents/file/${documentId}?token=${token}`, '_blank');
  };

  const handleDownload = (document) => {
    window.open(`${API_URL}/documents/file/${document.id}?token=${token}&download=1`, '_blank');
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(documentId);
      await api.delete(`${API_URL}/documents/${documentId}`, { headers });
      if (onDelete) {
        onDelete();
      }
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  if (documents.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '4rem', marginBottom: '16px', opacity: 0.3 }}>📄</div>
        <h3 style={{ color: '#4a5568', marginBottom: '8px' }}>No Documents Uploaded Yet</h3>
        <p style={{ color: '#718096' }}>Upload medical records, prescriptions, and other health documents for this patient.</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <h3 style={{ color: '#2d3748', marginBottom: '20px' }}>Health Documents</h3>

      {Object.entries(documentsByType).map(([type, docs]) => (
        <div key={type} style={{ marginBottom: '24px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {/* Section Header */}
          <button
            onClick={() => toggleSection(type)}
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor: 'white',
              color: '#2d3748',
              border: 'none',
              borderLeft: `4px solid ${DOCUMENT_TYPE_COLORS[type] || DOCUMENT_TYPE_COLORS.other}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: DOCUMENT_TYPE_COLORS[type] || DOCUMENT_TYPE_COLORS.other, display: 'flex' }}>
                {DOCUMENT_TYPE_ICONS[type] || DOCUMENT_TYPE_ICONS.other}
              </span>
              {DOCUMENT_TYPE_LABELS[type] || type}
              <span style={{ backgroundColor: '#edf2f7', color: '#4a5568', padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem' }}>
                {docs.length}
              </span>
            </span>
            <span style={{ fontSize: '0.9rem', color: '#a0aec0' }}>
              {expandedSections[type] ? '▲' : '▼'}
            </span>
          </button>

          {/* Section Content */}
          {expandedSections[type] && (
            <div style={{ width: '100%', overflow: 'hidden', padding: '0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '16px', color: '#4a5568', fontWeight: '600', width: '28%' }}>Document</th>
                    <th style={{ textAlign: 'left', padding: '16px', color: '#4a5568', fontWeight: '600', width: '9%' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '16px', color: '#4a5568', fontWeight: '600', width: '8%' }}>Size</th>
                    <th style={{ textAlign: 'left', padding: '16px', color: '#4a5568', fontWeight: '600', width: '22%' }}>Extracted Medical Data (JSON)</th>
                    <th style={{ textAlign: 'left', padding: '16px', color: '#4a5568', fontWeight: '600', width: '10%' }}>Tags</th>
                    <th style={{ textAlign: 'left', padding: '16px', color: '#4a5568', fontWeight: '600', width: '10%' }}>Clinical Notes</th>
                    <th style={{ textAlign: 'center', padding: '16px', color: '#4a5568', fontWeight: '600', width: '13%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc, index) => (
                    <tr key={doc.id}
                      style={{ borderBottom: index === docs.length - 1 ? 'none' : '1px solid #e2e8f0', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '16px', color: '#2d3748', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <span style={{ fontSize: '1.5rem', flexShrink: 0, marginTop: '2px' }}>{getFileIcon(doc.mime_type, doc.original_filename)}</span>
                          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontWeight: '500', marginBottom: '4px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {doc.original_filename}
                            </div>
                            <div style={{ color: '#718096', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                              Uploaded {new Date(doc.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px', color: '#718096', wordBreak: 'break-word', overflowWrap: 'break-word', verticalAlign: 'top' }}>
                        {doc.document_date ? new Date(doc.document_date).toLocaleDateString() : '-'}
                      </td>
                      <td style={{ padding: '16px', color: '#718096', wordBreak: 'break-word', overflowWrap: 'break-word', verticalAlign: 'top' }}>
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td style={{ padding: '16px', color: '#718096', fontSize: '0.85rem', wordBreak: 'break-word', overflowWrap: 'break-word', verticalAlign: 'top' }}>
                        <div
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                          onMouseEnter={(e) => handleSummaryMouseEnter(e, getExtractedMedicalData(doc.notes))}
                          onMouseLeave={handleSummaryMouseLeave}
                        >
                          <span style={{ cursor: 'help', borderBottom: '1px dashed #718096' }} title="Hover to view full JSON">
                            {getExtractedDataSummary(doc.notes)}
                          </span>
                          <button
                            onClick={() => {
                              const json = getExtractedMedicalData(doc.notes);
                              navigator.clipboard.writeText(json).catch(err => alert('Failed to copy: ' + err));
                            }}
                            title="Copy JSON to clipboard"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '2px', opacity: 0.7 }}
                            onMouseOver={(e) => e.target.style.opacity = 1}
                            onMouseOut={(e) => e.target.style.opacity = 0.7}
                          >📋</button>
                          <button
                            onClick={() => {
                              const json = getExtractedMedicalData(doc.notes);
                              downloadJson(json, doc.original_filename);
                            }}
                            title="Download JSON file"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '2px', opacity: 0.7 }}
                            onMouseOver={(e) => e.target.style.opacity = 1}
                            onMouseOut={(e) => e.target.style.opacity = 0.7}
                          >💾</button>
                        </div>
                      </td>
                      <td style={{ padding: '16px', color: '#718096', wordBreak: 'break-word', overflowWrap: 'break-word', verticalAlign: 'top' }}>
                        {doc.tags && doc.tags.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', alignItems: 'center' }}>
                            {doc.tags.map(tag => (
                              <span key={tag.id} style={{
                                background: tag.color + '20',
                                color: tag.color,
                                border: `1px solid ${tag.color}40`,
                                borderRadius: '10px',
                                padding: '1px 6px',
                                fontSize: '0.7rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td
                        title={getClinicalNotes(doc.notes)}
                        style={{ padding: '16px', color: '#718096', fontSize: '0.85rem',
                          wordBreak: 'break-word', overflowWrap: 'break-word', verticalAlign: 'top' }}
                      >
                        {getClinicalNotes(doc.notes)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
                          <IconButton
                            onClick={() => handleView(doc.id)}
                            title="View document"
                            hoverBg="#ebf8ff"
                            hoverColor="#2b6cb0"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                            </svg>
                          </IconButton>
                          <IconButton
                            onClick={() => handleDownload(doc)}
                            title="Download document"
                            hoverBg="#f0fff4"
                            hoverColor="#276749"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/>
                            </svg>
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(doc.id)}
                            disabled={deletingId === doc.id}
                            title="Delete document"
                            hoverBg="#fff5f5"
                            hoverColor="#c53030"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      {tooltip.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '600px',
            maxHeight: '400px',
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            pointerEvents: 'auto'
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {tooltip.json}
        </div>
      )}
    </div>
  );
}

export default DocumentList;
